import { useState, useCallback } from 'react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import {
  getCalculationHistory,
  getCalculationDetail,
} from '../services/calculationService';

interface CalculationRecord {
  calculation_id: number;
  appt_id: string;
  hospital_uuid: string;
  agreement_id: number;
  agreement_version: number;
  ats_data: Record<string, unknown>;
  input_parameter_values: Record<string, unknown>;
  calculated_parameter_values: Record<string, unknown>;
  hospital_share: number;
  ar_ap: number;
  validation_status: string;
  error_category: string | null;
  remarks: string | null;
  is_recalculation: boolean;
  parent_calculation_id: number | null;
  calculated_by: string;
  calculated_at: string;
  execution_time_ms: number | null;
}

export default function CalculationHistoryPage() {
  const [apptId, setApptId] = useState('');
  const [calculations, setCalculations] = useState<CalculationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Detail modal
  const [detailCalc, setDetailCalc] = useState<CalculationRecord | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  const handleSearch = useCallback(async () => {
    const trimmed = apptId.trim();
    if (!trimmed) return;
    setIsLoading(true);
    setError('');
    setCalculations([]);
    setHasSearched(true);

    try {
      const data = await getCalculationHistory(trimmed);
      setCalculations(data.calculations as unknown as CalculationRecord[]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to fetch history.');
    } finally {
      setIsLoading(false);
    }
  }, [apptId]);

  const handleViewDetails = useCallback(async (calcId: number) => {
    setIsLoadingDetail(true);
    setDetailError('');
    setDetailCalc(null);
    try {
      const data = await getCalculationDetail(calcId);
      setDetailCalc(data.calculation as unknown as CalculationRecord);
    } catch (err: any) {
      setDetailError(err?.response?.data?.error || err?.message || 'Failed to load details.');
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const parentIds = new Set(
    calculations.filter((c) => c.parent_calculation_id).map((c) => c.parent_calculation_id)
  );

  const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleString() : '—');
  const formatNum = (val: unknown): string => {
    if (val == null) return '—';
    const n = Number(val);
    return isNaN(n) ? String(val) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const statusClass = (status: string) => {
    if (status === 'OK') return 'status-active';
    if (status === 'NOT_OK') return 'status-inactive';
    return '';
  };

  return (
    <div className="calc-history-page">
      <div className="page-header">
        <h2>Calculation History</h2>
      </div>

      <section className="calc-history-search">
        <div className="calc-history-search-row">
          <Input
            label="Appointment ID"
            value={apptId}
            onChange={(e) => setApptId(e.target.value)}
            placeholder="Enter ApptID to search"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
          <Button onClick={handleSearch} isLoading={isLoading} disabled={!apptId.trim() || isLoading}>
            Search
          </Button>
        </div>
      </section>

      {error && <Alert type="error">{error}</Alert>}

      {isLoading ? (
        <LoadingSpinner message="Loading calculation history..." />
      ) : hasSearched && calculations.length === 0 && !error ? (
        <Alert type="info">No calculations found for this Appointment ID.</Alert>
      ) : calculations.length > 0 ? (
        <>
          <div className="calc-history-summary">
            <span>{calculations.length} calculation{calculations.length !== 1 ? 's' : ''} found</span>
          </div>

          <div className="calc-history-timeline">
            {calculations.map((calc, idx) => {
              const isRecalc = calc.is_recalculation;
              const hasChildren = parentIds.has(calc.calculation_id);
              return (
                <div
                  key={calc.calculation_id}
                  className={`timeline-item ${isRecalc ? 'timeline-recalc' : ''}`}
                >
                  <div className="timeline-marker">
                    <div className="timeline-dot" />
                    {idx < calculations.length - 1 && <div className="timeline-line" />}
                  </div>
                  <div className="timeline-card">
                    <div className="timeline-card-header">
                      <span className="timeline-calc-id">#{calc.calculation_id}</span>
                      <span className={`status-badge ${statusClass(calc.validation_status)}`}>
                        {calc.validation_status || 'PENDING'}
                      </span>
                      {isRecalc && (
                        <span className="recalc-badge" title={`Recalculation of #${calc.parent_calculation_id}`}>
                          ↻ Recalc of #{calc.parent_calculation_id}
                        </span>
                      )}
                      {hasChildren && (
                        <span className="recalc-parent-badge">Has recalculations</span>
                      )}
                    </div>
                    <div className="timeline-card-body">
                      <div className="timeline-field">
                        <span className="timeline-label">Date</span>
                        <span>{formatDate(calc.calculated_at)}</span>
                      </div>
                      <div className="timeline-field">
                        <span className="timeline-label">Calculated By</span>
                        <span>{calc.calculated_by}</span>
                      </div>
                      <div className="timeline-field">
                        <span className="timeline-label">Hospital Share</span>
                        <span>{formatNum(calc.hospital_share)}</span>
                      </div>
                      <div className="timeline-field">
                        <span className="timeline-label">AR/AP</span>
                        <span>{formatNum(calc.ar_ap)}</span>
                      </div>
                      {calc.error_category && (
                        <div className="timeline-field">
                          <span className="timeline-label">Error</span>
                          <span className="error-badge">{calc.error_category}</span>
                        </div>
                      )}
                    </div>
                    <div className="timeline-card-actions">
                      <Button size="sm" onClick={() => handleViewDetails(calc.calculation_id)}>
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {/* Detail Modal */}
      <Modal
        isOpen={!!detailCalc || isLoadingDetail || !!detailError}
        onClose={() => { setDetailCalc(null); setDetailError(''); }}
        title={detailCalc ? `Calculation #${detailCalc.calculation_id}` : 'Calculation Details'}
        size="lg"
      >
        {isLoadingDetail ? (
          <LoadingSpinner message="Loading details..." />
        ) : detailError ? (
          <Alert type="error">{detailError}</Alert>
        ) : detailCalc ? (
          <CalculationDetailView calc={detailCalc} />
        ) : null}
      </Modal>
    </div>
  );
}

function CalculationDetailView({ calc }: { calc: CalculationRecord }) {
  const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleString() : '—');
  const formatNum = (val: unknown): string => {
    if (val == null) return '—';
    const n = Number(val);
    return isNaN(n) ? String(val) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const atsData = (typeof calc.ats_data === 'object' && calc.ats_data) ? calc.ats_data : {};
  const inputParams = (typeof calc.input_parameter_values === 'object' && calc.input_parameter_values) ? calc.input_parameter_values : {};
  const calcParams = (typeof calc.calculated_parameter_values === 'object' && calc.calculated_parameter_values) ? calc.calculated_parameter_values : {};

  return (
    <div className="calc-detail">
      {/* Summary Grid */}
      <div className="calc-detail-grid">
        <div className="calc-detail-item">
          <label>Calculation ID</label>
          <span>{calc.calculation_id}</span>
        </div>
        <div className="calc-detail-item">
          <label>Appointment ID</label>
          <span>{calc.appt_id}</span>
        </div>
        <div className="calc-detail-item">
          <label>Agreement</label>
          <span>ID {calc.agreement_id} (v{calc.agreement_version})</span>
        </div>
        <div className="calc-detail-item">
          <label>Hospital Share</label>
          <span className="calc-detail-value-highlight">{formatNum(calc.hospital_share)}</span>
        </div>
        <div className="calc-detail-item">
          <label>AR/AP</label>
          <span className="calc-detail-value-highlight">{formatNum(calc.ar_ap)}</span>
        </div>
        <div className="calc-detail-item">
          <label>Validation Status</label>
          <span className={`status-badge ${calc.validation_status === 'OK' ? 'status-active' : calc.validation_status === 'NOT_OK' ? 'status-inactive' : ''}`}>
            {calc.validation_status}
          </span>
        </div>
        {calc.error_category && (
          <div className="calc-detail-item">
            <label>Error Category</label>
            <span className="error-badge">{calc.error_category}</span>
          </div>
        )}
        {calc.remarks && (
          <div className="calc-detail-item calc-detail-item-full">
            <label>Remarks</label>
            <span>{calc.remarks}</span>
          </div>
        )}
        <div className="calc-detail-item">
          <label>Calculated By</label>
          <span>{calc.calculated_by}</span>
        </div>
        <div className="calc-detail-item">
          <label>Calculated At</label>
          <span>{formatDate(calc.calculated_at)}</span>
        </div>
        <div className="calc-detail-item">
          <label>Execution Time</label>
          <span>{calc.execution_time_ms != null ? `${calc.execution_time_ms}ms` : '—'}</span>
        </div>
        {calc.is_recalculation && (
          <div className="calc-detail-item">
            <label>Recalculation Of</label>
            <span>#{calc.parent_calculation_id}</span>
          </div>
        )}
      </div>

      {/* ATS Data */}
      <details className="calc-detail-section">
        <summary>ATS Data Snapshot ({Object.keys(atsData).length} fields)</summary>
        <div className="calc-detail-kv-table">
          <table className="data-table">
            <thead>
              <tr><th>Field</th><th>Value</th></tr>
            </thead>
            <tbody>
              {Object.entries(atsData).map(([key, val]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{val != null ? String(val) : '—'}</td>
                </tr>
              ))}
              {Object.keys(atsData).length === 0 && (
                <tr><td colSpan={2} className="table-empty-cell">No ATS data available</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>

      {/* Input Parameters */}
      <details className="calc-detail-section">
        <summary>Input Parameter Values ({Object.keys(inputParams).length} parameters)</summary>
        <div className="calc-detail-kv-table">
          <table className="data-table">
            <thead>
              <tr><th>Parameter</th><th>Value</th></tr>
            </thead>
            <tbody>
              {Object.entries(inputParams).map(([key, val]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{val != null ? String(val) : '—'}</td>
                </tr>
              ))}
              {Object.keys(inputParams).length === 0 && (
                <tr><td colSpan={2} className="table-empty-cell">No input parameters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>

      {/* Calculated Parameters */}
      <details className="calc-detail-section">
        <summary>Calculated Parameter Values ({Object.keys(calcParams).length} parameters)</summary>
        <div className="calc-detail-kv-table">
          <table className="data-table">
            <thead>
              <tr><th>Parameter</th><th>Value</th></tr>
            </thead>
            <tbody>
              {Object.entries(calcParams).map(([key, val]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{formatNum(val)}</td>
                </tr>
              ))}
              {Object.keys(calcParams).length === 0 && (
                <tr><td colSpan={2} className="table-empty-cell">No calculated parameters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
